import type { Place, SatellitePlace } from '@types';
import { AppContext } from 'context/app';
import { useKioskManager } from 'hooks/useKioskManager';
import { useNetworkManager } from 'hooks/useNetworkManager';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ButtonStyled from '../../shared/button/Button';
import { Card } from '../card/Card';
import Select from '../select/Select';
import LocationListItem from './LocationListItem';
import styles from './locationSelector.module.scss';

interface LocationSelectorProps {
  onSetLocation: (location: Place | SatellitePlace) => void;
  setting: 'starting' | 'destination';
  setSearchResults: (params: { [key: string]: any }) => void;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  onSetLocation,
  setting,
  setSearchResults,
}) => {
  const { t } = useTranslation('common');
  const {
    availableSites,
    campusName,
    locationSearchText,
    navDestinationPlace,
    navStartingPlace,
  } = useContext(AppContext);
  const { placesData, siteName, isNetworkLoading, satellitePlacesData } =
    useNetworkManager();
  const { kioskMode } = useKioskManager();
  const [selectedCampus, setSelectedCampus] = useState<string | null>();
  const [activeLocation, setActiveLocation] = useState<number | null>(null);

  const startingPlaceId = useMemo(() => {
    if (!navStartingPlace) return null;
    if (navStartingPlace) return navStartingPlace.id;
  }, [navStartingPlace]);

  const destinationPlaceId = useMemo(() => {
    if (!navDestinationPlace) return null;
    return navDestinationPlace.id;
  }, [navDestinationPlace]);

  const handleSetLocation = (location: number) => {
    const place =
      placesData?.find((l) => l.id === location) ??
      satellitePlacesData?.find((l) => l.id === location);
    if (place) {
      onSetLocation(place);
    }
  };

  const handleCampusChange = (value: string | number) => {
    setSelectedCampus(value as string);
  };

  const campusFilteredLocations = useMemo(() => {
    const filtered = placesData?.filter((location) => {
      if (kioskMode) {
        if (location.searchable === 'never' && location.visible === 'never') {
          return false;
        }
      } else if (
        location.visible === 'never' ||
        location.searchable !== 'always'
      ) {
        return false;
      }

      if (
        startingPlaceId &&
        !destinationPlaceId &&
        setting === 'destination' &&
        +location.id === +startingPlaceId
      ) {
        return false;
      }
      if (
        destinationPlaceId &&
        !startingPlaceId &&
        setting === 'starting' &&
        +location.id === +destinationPlaceId
      ) {
        return false;
      }
      if (!selectedCampus || !availableSites) {
        return true;
      }
      return +location.location.site === +selectedCampus;
    });
    return filtered ?? [];
  }, [
    availableSites,
    destinationPlaceId,
    kioskMode,
    placesData,
    selectedCampus,
    startingPlaceId,
  ]);

  const searchResults = useMemo(() => {
    const campusPlaces = locationSearchText
      ? campusFilteredLocations.filter((place) =>
          place.tags.some((t) =>
            t.toLowerCase().includes(locationSearchText.toLowerCase()),
          ),
        )
      : campusFilteredLocations;
    const satellitePlaces = locationSearchText
      ? satellitePlacesData?.filter((place) => {
          if (
            startingPlaceId &&
            !destinationPlaceId &&
            setting === 'destination' &&
            +place.id === +startingPlaceId
          ) {
            console.log(startingPlaceId, destinationPlaceId, setting);
            return false;
          }
          if (
            destinationPlaceId &&
            !startingPlaceId &&
            setting === 'starting' &&
            +place.id === +destinationPlaceId
          ) {
            return false;
          }
          return place.name
            .toLowerCase()
            .includes(locationSearchText.toLowerCase());
        })
      : satellitePlacesData;
    // eslint-disable-next-line no-extra-parens
    const results = [...campusPlaces, ...(satellitePlaces ?? [])];
    return results;
  }, [
    satellitePlacesData,
    campusFilteredLocations,
    locationSearchText,
    startingPlaceId,
    destinationPlaceId,
  ]);

  useEffect(() => {
    setSearchResults({
      value: locationSearchText,
      results: searchResults.length,
      setting,
    });
  }, [searchResults, locationSearchText, setting, setSearchResults]);

  const options = useMemo(() => {
    const emptyOption = {
      value: '',
      label: t('campusNavigation.form.allLocations'),
      key: 'empty',
    };

    if (!availableSites) return [emptyOption];
    const allOptions = availableSites.map((site) => ({
      value: site.id,
      label: site.name,
    }));
    return [
      emptyOption,
      ...allOptions.filter(
        (value, index) => allOptions.indexOf(value) === index,
      ),
    ];
  }, [availableSites, t]);

  const handleSetActive = (locationId: number) => {
    if (activeLocation === locationId) {
      setActiveLocation(null);
    } else {
      setActiveLocation(locationId);
    }
  };

  const limitIncrement: number = 10;
  const [limit, setLimit] = useState<number>(limitIncrement);

  if (locationSearchText && !searchResults.length) {
    return <p>{t('campusNavigation.form.noResults')}</p>;
  }

  if (isNetworkLoading) return <p>{t('labels.loading')}</p>;

  return (
    <Card>
      <span className={styles.boldSectionHeader}>
        {campusName && t('campusNavigation.form.campusLocations', { siteName })}
      </span>
      {availableSites && (
        <Select
          options={options}
          onChange={handleCampusChange}
          placeholder={t('campusNavigation.form.allLocations')}
        />
      )}
      {searchResults
        ?.slice(0, limit)
        .map((location) => (
          <LocationListItem
            key={location.id}
            place={location}
            activeLocation={activeLocation}
            setLocation={handleSetLocation}
            onClick={() => handleSetActive(location.id)}
            setting={setting}
          />
        ))}
      {searchResults.length > limit && (
        <ButtonStyled
          onClick={() => setLimit(limit + limitIncrement)}
          uppercase
        >
          {t('campusNavigation.form.showMore')}
        </ButtonStyled>
      )}
    </Card>
  );
};

export default LocationSelector;
